import { useEffect, useRef } from "react";
import { orbStore } from "@/lib/orb";
import { thump } from "@/lib/audio";
import { ORB_THEMES } from "@/lib/orbThemes";

const PARAMS = {
  idle: { speed: 0.35, rot: 0.08, amp: 0.12, hue: 0, glow: 0.55, period: 1.05 },
  ascolto: { speed: 1.1, rot: 0.25, amp: 0.55, hue: -20, glow: 0.8, period: 0.78 },
  elaborazione: { speed: 2.4, rot: 0.9, amp: 0.35, hue: 60, glow: 0.85, period: 0.46 },
  parlato: { speed: 1.4, rot: 0.3, amp: 0.9, hue: 20, glow: 1, period: 0.66 },
};

const RIBBONS = [
  { tilt: 0.25, ph: 0 }, { tilt: 0.55, ph: 1.3 }, { tilt: 0.85, ph: 2.6 },
  { tilt: 0.4, ph: 3.9 }, { tilt: 0.7, ph: 5.1 }, { tilt: 0.15, ph: 0.7 },
];
const TICKS = 96;
const TAU = Math.PI * 2;

const lerp = (a, b, t) => a + (b - a) * t;
const disc = (g, x, y, r) => { g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill(); };

function createState() {
  return {
    cur: { ...PARAMS.idle }, level: 0, t: 0, last: performance.now(), warmth: 0,
    hb: { phase: 0, last: 0 },
    particles: Array.from({ length: 46 }, () => ({ a: Math.random() * TAU, r: 0.55 + Math.random() * 0.6, s: 0.2 + Math.random() * 0.6, z: Math.random() })),
    noise: Array.from({ length: TICKS }, () => Math.random()),
  };
}

// Eases animation params towards the current orb state.
function easeParams(S, st) {
  const target = PARAMS[st] || PARAMS.idle;
  for (const k of Object.keys(S.cur)) if (k !== "period") S.cur[k] = lerp(S.cur[k], target[k], 0.06);
  // Heart speeds up quickly, calms down slowly after Aurora finishes.
  S.cur.period = lerp(S.cur.period, target.period, target.period < S.cur.period ? 0.05 : 0.006);
}

function updateLevel(S, st, now) {
  orbStore.bump *= 0.92;
  const raw = Math.max(orbStore.level, orbStore.bump);
  const idleLevel = 0.08 + Math.sin(now / 900) * 0.06 + orbStore.bump;
  S.level = lerp(S.level, st === "idle" ? idleLevel : raw, 0.25);
}

// Advances the double-beat heartbeat and plays the thumps. Returns beat intensity.
function heartbeat(S, st, dt) {
  const hb = S.hb;
  hb.phase += dt / S.cur.period;
  if (hb.phase >= 1) hb.phase -= 1;
  const p = hb.phase;
  if (orbStore.heart && st !== "parlato") {
    if (hb.last < 0.03 && p >= 0.03) thump(1);
    if (hb.last < 0.21 && p >= 0.21) thump(0.6);
  }
  hb.last = p;
  return Math.exp(-((p - 0.03) ** 2) / 0.0012) + 0.65 * Math.exp(-((p - 0.21) ** 2) / 0.0018);
}

function moodTarget(st, now) {
  if (st === "elaborazione") return -1;
  return orbStore.moodUntil > now ? 1 : 0;
}

function drawCore(g, f) {
  const { cx, cy, R, T, S, beat } = f;
  const core = g.createRadialGradient(cx, cy, 0, cx, cy, R * (1.25 + S.level * 0.3));
  core.addColorStop(0, `rgba(${T.core0},${0.22 * S.cur.glow + S.level * 0.15 + beat * 0.22})`);
  core.addColorStop(0.5, `rgba(${T.core1},${0.12 * S.cur.glow})`);
  core.addColorStop(1, "rgba(3,7,18,0)");
  g.fillStyle = core;
  disc(g, cx, cy, R * 1.6);
}

function drawMood(g, f) {
  const { cx, cy, R, S, beat } = f;
  const w = S.warmth;
  if (Math.abs(w) <= 0.02) return;
  const tint = w > 0 ? "255,170,60" : "70,140,255";
  const mood = g.createRadialGradient(cx, cy, R * 0.2, cx, cy, R * 1.5);
  mood.addColorStop(0, `rgba(${tint},${Math.abs(w) * (0.32 + beat * 0.12)})`);
  mood.addColorStop(0.7, `rgba(${tint},${Math.abs(w) * 0.12})`);
  mood.addColorStop(1, `rgba(${tint},0)`);
  g.globalCompositeOperation = "lighter";
  g.fillStyle = mood;
  disc(g, cx, cy, R * 1.5);
  g.globalCompositeOperation = "source-over";
}

function ribbonPath(g, f, rb, i, layer, rotA, squash) {
  const { cx, cy, R, S } = f;
  const { t, level, cur } = S;
  g.beginPath();
  for (let s = 0; s <= 90; s++) {
    const th = (s / 90) * TAU;
    const wob = Math.sin(th * 3 + t * 2 + rb.ph + layer * 0.4) * (0.05 + cur.amp * level * 0.32)
      + Math.sin(th * 5 - t * 3 + i) * level * cur.amp * 0.14
      + Math.sin(th * 11 + t * 16 + i) * level * cur.amp * 0.05;
    const r = R * (0.92 + wob + layer * 0.025);
    const x = r * Math.cos(th), y = r * Math.sin(th) * squash;
    const xr = cx + x * Math.cos(rotA) - y * Math.sin(rotA);
    const yr = cy + x * Math.sin(rotA) + y * Math.cos(rotA);
    if (s === 0) g.moveTo(xr, yr); else g.lineTo(xr, yr);
  }
}

function drawRibbons(g, f) {
  const { cx, cy, R, T, S } = f;
  g.globalCompositeOperation = "lighter";
  RIBBONS.forEach((rb, i) => {
    const grad = g.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
    grad.addColorStop(0, T.ribbons[i][0]); grad.addColorStop(1, T.ribbons[i][1]);
    const rotA = S.t * S.cur.rot * (i % 2 ? -1 : 1) * 2 + rb.ph;
    const squash = Math.cos(rb.tilt * Math.PI + S.t * 0.3 * (1 + i * 0.1));
    for (let layer = 0; layer < 3; layer++) {
      ribbonPath(g, f, rb, i, layer, rotA, squash);
      g.strokeStyle = grad;
      g.globalAlpha = (0.45 - layer * 0.12) * (0.6 + S.cur.glow * 0.4);
      g.lineWidth = 2.2 + layer * 2.5 + S.level * 3;
      g.stroke();
    }
  });
  g.globalAlpha = 1;
}

function drawWaves(g, f) {
  const { cx, cy, R, Rb, T, S, st } = f;
  if (S.level <= 0.04 || st === "idle") return;
  const cols = [`rgba(${T.waves[0]},0.75)`, `rgba(${T.waves[1]},0.65)`, `rgba(${T.waves[2]},0.55)`];
  for (let w = 0; w < 3; w++) {
    const harm = 6 + w * 3, rr = R * (1.06 + w * 0.07), dir = w % 2 ? -1 : 1;
    g.beginPath();
    for (let s = 0; s <= 140; s++) {
      const th = (s / 140) * TAU;
      const a = S.level * Rb * (0.15 - w * 0.035) * Math.sin(th * harm + S.t * (5 + w * 2) * dir) * (0.6 + 0.4 * Math.sin(th * 3 - S.t * 4));
      const x = cx + Math.cos(th) * (rr + a), y = cy + Math.sin(th) * (rr + a);
      if (s === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.strokeStyle = cols[w];
    g.lineWidth = 1.6;
    g.stroke();
  }
}

function drawTicks(g, f, now) {
  const { Rb, T, S, st } = f;
  const r0 = Rb * 1.28;
  for (let k = 0; k < TICKS; k++) {
    const a = (k / TICKS) * TAU;
    const n = S.noise[k] * 0.6 + 0.4 * Math.sin(now / 160 + k);
    const len = 4 + (st === "idle" ? 2 : S.level * 26 * Math.abs(n));
    const major = k % 8 === 0;
    g.strokeStyle = major ? `rgba(${T.accent},0.75)` : `rgba(${T.tick},0.45)`;
    g.lineWidth = major ? 2 : 1;
    g.beginPath();
    g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
    g.lineTo(Math.cos(a) * (r0 + len), Math.sin(a) * (r0 + len));
    g.stroke();
  }
}

function drawHud(g, f, now) {
  const { cx, cy, Rb, T, S } = f;
  g.save(); g.translate(cx, cy);
  g.rotate(S.t * 0.15);
  drawTicks(g, f, now);
  g.rotate(-S.t * 0.45);
  g.strokeStyle = `rgba(${T.arc},0.5)`; g.lineWidth = 2;
  for (let k = 0; k < 4; k++) {
    g.beginPath(); g.arc(0, 0, Rb * 1.52, k * Math.PI / 2 + 0.2, k * Math.PI / 2 + 1.1); g.stroke();
  }
  g.strokeStyle = `rgba(${T.ring},0.35)`; g.lineWidth = 1;
  g.beginPath(); g.arc(0, 0, Rb * 1.62, 0, TAU); g.stroke();
  g.restore();
}

function drawParticles(g, f, dt) {
  const { cx, cy, R, T, S } = f;
  S.particles.forEach((p) => {
    p.a += dt * p.s * (0.4 + S.cur.speed * 0.6);
    const rr = R * (p.r + S.level * 0.15);
    g.fillStyle = p.z > 0.7 ? `rgba(${T.part[0]},0.8)` : `rgba(${T.part[1]},0.75)`;
    disc(g, cx + Math.cos(p.a) * rr * 1.3, cy + Math.sin(p.a) * rr * (0.5 + p.z * 0.6), 0.6 + p.z * 1.4);
  });
  g.globalCompositeOperation = "source-over";
}

// One animation frame: update state, then draw every layer.
function renderFrame(g, S, size, hud, now) {
  const dt = Math.min(0.05, (now - S.last) / 1000);
  S.last = now;
  const st = orbStore.state;
  easeParams(S, st);
  updateLevel(S, st, now);
  S.t += dt * S.cur.speed;
  const beat = heartbeat(S, st, dt);
  const speaking = st === "parlato";
  const scale = 1 + beat * (speaking ? 0.02 : 0.075) + (speaking ? S.level * 0.09 : 0);
  const Rb = size * 0.3;
  const f = { cx: size / 2, cy: size / 2, Rb, R: Rb * scale, T: ORB_THEMES[orbStore.theme] || ORB_THEMES.aurora, S, st, beat };
  S.warmth = lerp(S.warmth, moodTarget(st, now), 0.025);
  g.clearRect(0, 0, size, size);
  drawCore(g, f);
  drawMood(g, f);
  drawRibbons(g, f);
  drawWaves(g, f);
  if (hud) drawHud(g, f, now);
  drawParticles(g, f, dt);
}

export const AuroraOrb = ({ size = 320, hud = true, testId = "sfera-aurora-orb" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const g = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    g.scale(dpr, dpr);
    const S = createState();
    let raf;
    const loop = (now) => { renderFrame(g, S, size, hud, now); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [size, hud]);

  return <canvas ref={ref} data-testid={testId} style={{ width: size, height: size }} className="block select-none pointer-events-none" aria-label="Sfera Aurora" />;
};
